import { IconType } from 'react-icons';
import { FiList, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { LiaChalkboardTeacherSolid } from 'react-icons/lia';
import { LuFileQuestion, LuLogOut } from 'react-icons/lu';
import { PiStudent } from 'react-icons/pi';
import { BiBookReader } from 'react-icons/bi';
import { GoOrganization } from 'react-icons/go';
import { useLocation } from 'react-router';
import { useEffect, useMemo } from 'react';

import {
  Box,
  BoxProps,
  CloseButton,
  Flex,
  useDisclosure,
  Text,
  Icon,
  Collapse,
} from '@chakra-ui/react';

import SidebarItem from './SidebarItem';
import { useAuth } from '@/context/useAuth';
import { useSidebar } from '@/context/SidebarContext';
import LogoutConfirmationDialog from '@/components/Modals/LogoutConfirmationDialog';
import { t } from 'i18next';
import { FaUserCircle } from 'react-icons/fa';
import { openInNewTab } from '@/utils/openInNewTab';
import { useTrimmedFullName } from '@/utils/useTrimmedFullName';
interface LinkItemProps {
  name: string;
  icon: IconType;
  to: string;
  adminOnly?: boolean;
  requiredPermission?: string;
  onClick?: () => void;
}

interface NavGroupProps {
  title: string;
  items: LinkItemProps[];
  icon: IconType;
}

interface SidebarProps extends BoxProps {
  onClose: () => void;
}

const Sidebar = ({ onClose, ...rest }: SidebarProps) => {
  const { hasPermission } = useAuth();
  const { logout, fullName } = useAuth();
  const trimmedFullName = useTrimmedFullName(fullName);

  const {
    isOpen: isLogoutDialogOpen,
    onOpen: onLogoutDialogOpen,
    onClose: onLogoutDialogClose,
  } = useDisclosure();

  const location = useLocation();
  const { expandedGroups, toggleGroup, expandGroup } = useSidebar();

  const borderColor = 'gray.200';
  const activeColor = 'teal.600';

  const navGroups = useMemo<NavGroupProps[]>(
    () => [
      {
        title: '学習者',
        icon: PiStudent,
        items: [
          {
            name: '学習者一覧',
            icon: FiList,
            to: '/students',
            requiredPermission: 'students.view',
          },
        ],
      },
      {
        title: '団体',
        icon: GoOrganization,
        items: [
          {
            name: '団体一覧',
            icon: FiList,
            to: '/organizations',
            requiredPermission: 'organizations.view',
          },
        ],
      },
      {
        title: '教材',
        icon: LuFileQuestion,
        items: [
          {
            name: '問題一覧',
            icon: LuFileQuestion,
            to: '/questions',
            requiredPermission: 'teachers.view',
          },
          {
            name: 'テスト一覧',
            icon: FiList,
            to: '/quiz',
            requiredPermission: 'exams.view',
          },
          {
            name: 'デジタル教材',
            icon: BiBookReader,
            to: '/digital-teaching-materials',
          },
        ],
      },
      {
        title: '運用',
        icon: LiaChalkboardTeacherSolid,
        items: [
          {
            name: '管理者一覧',
            icon: FiList,
            to: '/teachers',
            requiredPermission: 'teachers.view',
          },
        ],
      },
    ],
    [],
  );

  useEffect(() => {
    // Function to check if a group contains the active page
    const isGroupActive = (group: NavGroupProps) => {
      return group.items.some(
        (item) => location.pathname.startsWith(item.to) && item.to !== '#',
      );
    };

    navGroups.forEach((group) => {
      if (isGroupActive(group)) {
        expandGroup(group.title);
      }
    });
  }, [location.pathname, expandGroup, navGroups]);

  return (
    <Box
      transition="all 0.3s ease"
      bg="white"
      borderRight="1px"
      borderRightColor="gray.200"
      w={{ base: 'full', md: 60 }}
      pos="fixed"
      h="100%"
      overflowY="auto"
      {...rest}
    >
      <Flex alignItems="center" m="4" justifyContent="flex-end">
        <CloseButton display={{ base: 'flex', md: 'none' }} onClick={onClose} />
      </Flex>
      {/* Mobile user info */}
      <Flex
        pb="4"
        pl="4"
        alignItems="center"
        borderBottom="1px"
        borderBottomColor={borderColor}
        display={{ base: 'flex', md: 'none' }}
      >
        <FaUserCircle size={40} />
        <Box ml="4">
          <Text fontSize="lg" fontWeight="semibold">
            {`${t('welcome')} ${trimmedFullName} さん`}
          </Text>
        </Box>
      </Flex>
      <Flex direction="column" h="calc(100% - 121px)" justify="space-between">
        <Box py="2" overflowY="auto" flex="1">
          {navGroups.map((group) => {
            const filteredItems = group.items.filter(
              (item) =>
                !item.requiredPermission ||
                hasPermission(item.requiredPermission),
            );

            if (filteredItems.length === 0) return null;

            const isExpanded = expandedGroups[group.title] === true;

            return (
              <Box key={group.title} mb="2" position="relative" px="2">
                <Flex
                  px="4"
                  py="2"
                  alignItems="center"
                  cursor="pointer"
                  onClick={() => toggleGroup(group.title)}
                  borderRadius="md"
                  transition="all 0.2s ease"
                  _hover={{ bg: 'gray.100' }}
                >
                  <Flex alignItems="center" gap="3" flex="1">
                    <Icon
                      as={group.icon}
                      fontSize="20px"
                      transition="all 0.2s"
                    />
                    <Text fontSize="lg" transition="all 0.2s">
                      {group.title}
                    </Text>
                  </Flex>
                  <Icon
                    as={isExpanded ? FiChevronUp : FiChevronDown}
                    color={isExpanded ? activeColor : 'gray.500'}
                    fontSize="18px"
                    transition="transform 0.2s"
                    transform={isExpanded ? 'scale(1.2)' : 'scale(1)'}
                  />
                </Flex>

                {/* Vertical line from headline */}
                {isExpanded && (
                  <Box
                    position="absolute"
                    left="8"
                    top="40px"
                    bottom="0"
                    width="2px"
                    bg={'orange.100'}
                    zIndex="0"
                    opacity="0.5"
                  />
                )}

                <Collapse in={isExpanded} animateOpacity>
                  <Box pl="3" pt="1">
                    {filteredItems.map((item) => (
                      <SidebarItem
                        key={item.name}
                        icon={item.icon}
                        to={item.to}
                        onClick={item.onClick}
                        mb="1"
                        ml="3"
                        zIndex="1"
                      >
                        {item.name}
                      </SidebarItem>
                    ))}
                  </Box>
                </Collapse>
              </Box>
            );
          })}
          {/* Logout button */}
          <Box px="4" mb="4">
            <SidebarItem
              icon={LuLogOut}
              to="#"
              onClick={onLogoutDialogOpen}
              _hover={{ bg: 'red.50', color: 'red.600' }}
              color="red.500"
              borderRadius="md"
              fontWeight="medium"
            >
              ログアウト
            </SidebarItem>
          </Box>
        </Box>

        {/* Footer section */}
        <Box py="2" px="4" borderTop="1px" borderTopColor={borderColor}>
          <Flex
            justifyContent="space-between"
            direction={{ base: 'row', md: 'column' }}
            gap={{ base: '0', md: '3' }}
          >
            <Text
              fontSize="sm"
              _hover={{
                textDecoration: 'underline',
                cursor: 'pointer',
                color: 'teal.500',
              }}
              onClick={() => openInNewTab('/privacy-policy')}
              transition="all 0.2s"
            >
              {t('privacy-policy')}
            </Text>
            <Text
              fontSize="sm"
              _hover={{
                textDecoration: 'underline',
                cursor: 'pointer',
                color: 'teal.500',
              }}
              onClick={() => openInNewTab('/terms')}
              transition="all 0.2s"
            >
              {t('terms-of-usage')}
            </Text>
            <Text
              fontSize="sm"
              _hover={{
                textDecoration: 'underline',
                cursor: 'pointer',
                color: 'teal.500',
              }}
              onClick={() => openInNewTab('/law')}
              transition="all 0.2s"
            >
              {t('law')}
            </Text>
          </Flex>
        </Box>
      </Flex>

      <LogoutConfirmationDialog
        isOpen={isLogoutDialogOpen}
        onClose={onLogoutDialogClose}
        onConfirm={() => {
          onLogoutDialogClose();
          logout();
        }}
      />
    </Box>
  );
};

export default Sidebar;
